import { Helmet } from "react-helmet-async";
import { PageHero } from "@/components/shared/PageHero";
import { ServiceSidebar } from "@/components/services/ServiceSidebar";
import { CTASection } from "@/components/shared/CTASection";
import { ServiceFAQ } from "@/components/services/ServiceFAQ";
import { Leaf, Sun, Snowflake, Trash2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import cleanupImage from "@/assets/services/seasonal-cleanup.jpg";

const features = [
  {
    icon: Sun,
    title: "Spring Cleanup",
    description: "Clear winter debris, dead leaves, and branches to prepare your lawn for the growing season.",
  },
  {
    icon: Leaf,
    title: "Fall Cleanup",
    description: "Remove fallen leaves and prepare your lawn and beds for winter dormancy.",
  },
  {
    icon: Trash2,
    title: "Debris Removal",
    description: "We haul away all debris—leaves, branches, and dead plant material—leaving your yard spotless.",
  },
  {
    icon: Snowflake,
    title: "Winter Preparation",
    description: "Cut back perennials, clean beds, and apply final mowing to protect your lawn through winter.",
  },
];

const cleanupFaqs = [
  { question: "What's included in spring cleanup?", answer: "Spring cleanup includes removing dead leaves, branches, and winter debris from lawns and beds, raking out thatch, edging beds, and preparing your property for the growing season." },
  { question: "What's included in fall cleanup?", answer: "Fall cleanup includes leaf removal from lawn and beds, cutting back perennials, final lawn mowing at appropriate height, and preparing beds for winter." },
  { question: "Do you remove the debris you collect?", answer: "Yes, all debris collected during cleanup is hauled away. You won't have to worry about bags or disposal." },
  { question: "How many fall cleanups do you recommend?", answer: "We typically recommend 1-2 fall cleanups depending on tree coverage. Properties with many trees may need an early and late fall cleanup." },
  { question: "When should I schedule spring cleanup?", answer: "Schedule spring cleanup as soon as the snow melts and ground thaws, typically late March through April in Minnesota." },
  { question: "When should I schedule fall cleanup?", answer: "Fall cleanup is best done after most leaves have fallen, typically late October through November in Minnesota." },
  { question: "Can you clean up after a storm?", answer: "Yes, we offer storm cleanup services to remove fallen branches and debris from your property." },
  { question: "Do you offer ongoing seasonal maintenance?", answer: "Yes! We can combine seasonal cleanups with regular lawn maintenance for complete year-round care." },
];

const SpringFallCleanups = () => {
  return (
    <>
      <Helmet>
        <title>Spring & Fall Cleanups | Riekels Lawn Care & Landscaping</title>
        <meta
          name="description"
          content="Professional spring and fall cleanup services in the West Twin Cities Metro, MN. Leaf removal, debris clearing, and seasonal prep. Call (763) 245-7813."
        />
      </Helmet>
      <main>
        <PageHero
          badge="Our Services"
          title="Spring & Fall Cleanups"
          subtitle="Seasonal cleanup services to keep your property looking great year-round."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
              <div className="lg:col-span-1">
                <ServiceSidebar />
              </div>
              <div className="lg:col-span-3">
                <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={cleanupImage} 
                    alt="Professional seasonal lawn cleanup" 
                    className="w-full h-64 md:h-80 object-cover object-center"
                  />
                </div>

                <div className="prose prose-lg max-w-none mb-12">
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-6">
                    Prepare Your Property for Every Season
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Minnesota's changing seasons require proper lawn and landscape care. Our spring and 
                    fall cleanup services ensure your property is prepared for the growing season or 
                    protected for winter dormancy.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    From clearing winter debris in spring to removing mountains of fallen leaves in fall, 
                    our crews have the equipment and expertise to handle properties of any size. We leave 
                    your lawn and beds clean, healthy, and ready for what's next.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {features.map((feature, index) => (
                    <article key={index} className="feature-card">
                      <div className="icon-circle mb-4">
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </article>
                  ))}
                </div>

                <div className="mt-12 bg-secondary rounded-2xl p-8 text-center">
                  <Leaf className="w-12 h-12 mx-auto mb-4 text-secondary-foreground" />
                  <h3 className="text-2xl font-heading font-bold text-secondary-foreground mb-3">
                    Schedule Your Seasonal Cleanup
                  </h3>
                  <p className="text-secondary-foreground/80 mb-6 max-w-lg mx-auto">
                    Don't let debris pile up. Contact us to schedule spring or fall cleanup.
                  </p>
                  <Link to="/contact">
                    <Button size="lg" className="bg-white hover:bg-white/90 text-secondary font-semibold">
                      Request a Quote <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={cleanupFaqs} />

        <CTASection />
      </main>
    </>
  );
};

export default SpringFallCleanups;
